<template>
  <div
    v-if="isAppReady"
    id="app"
    :class="{'is-animating': isAnimating}"
    @click="resetAppTimeout"
    :style="styleVars"
  >
    <heat-map-timeline 
    v-if="showPlayback"
      :sessionPlayback="sessionPlayback"
    />
    <heat-map-info
      v-if="showPlayback"
      :title="`Session Playback: ${playbackId}`"
      :interactions="`${playbackIndex}/${sessionPlayback.interactions ? sessionPlayback.interactions.length : 0}`"
    />
    <heat-map v-if="showHeat" />
    <div class="transition-overlay"/>

    <transition name="fade">
      <div v-show="isBottomBarVisible" class="overlay">
        <div class="overlay__inner"/>
        <div class="overlay__outer"/>
        <div class="overlay__sidebar"/>
      </div>
    </transition>

    <transition name="fade">
      <router-view class="router-view"/>
    </transition>

    <v-background :source="$store.getters.activeBackground"/>
    <bottom-bar :nav-items="$store.getters.activeBottomBar"/>
    <sub-bottom-bar :nav-items="$store.getters.activeSubBottomBar"/>
    <v-slides :slides="$store.getters.activeSlides"/>
    <v-tags :tags="$store.getters.activePageTags"/>
    <masterplan-tags :tags="$store.getters['masterplan/activeTags']"/>
    <living-option-tags :living-options="$store.getters['property/activeLivingOptionTags']"/>
    <living-option-subsections :subsections="$store.getters['property/activeLivingOptionSubsections']"/>
    <living-option-properties :properties="$store.getters['property/activeLivingOptionProperties']"/>
    <property-modal :property="$store.getters.activePropertyModal"/>
    <property-slider :slides="$store.getters.activePropertySlides"/>
    <right-sidebar :content="$store.getters['sidebar/activeSidebar']"/>
    <reference-tag-slider :tags="referenceTags"/>

    <transition name="fast-fade">
      <issuu-modal
        v-if="$store.state.actions.showIssuuUrl"
        :url="$store.state.actions.showIssuuUrl"
      />
    </transition>

    <transition name="fast-fade">
      <asset-viewer
        v-if="$store.state.actions.showAssetSource"
        :payload="$store.state.actions.showAssetSource"
        :tag="$store.state.actions.showAssetTag"
        :virtualTourUrl="$store.state.actions.showAssetvirtualTourUrl"
      />
    </transition>

    <transition name="fast-fade">
      <virtual-tour
        v-if="$store.getters['property/activeVirtualTourUrl']"
        :url="$store.getters['property/activeVirtualTourUrl']"
      />
    </transition>

    <NotAvailableTag />
  </div>
</template>
<script>
import AssetViewer from '@/components/AssetViewer'
import BottomBar from '@/components/BottomBar'
import HeatMap from '@/components/HeatMap'
import HeatMapInfo from '@/components/HeatMapInfo'
import HeatMapTimeline from '@/components/HeatMapTimeline'
import IssuuModal from '@/components/IssuuModal'
import LivingOptionProperties from '@/components/LivingOptionProperties'
import LivingOptionSubsections from '@/components/LivingOptionSubsections'
import LivingOptionTags from '@/components/LivingOptionTags'
import MasterplanTags from '@/components/MasterplanTags'
import PropertyModal from '@/components/PropertyModal'
import PropertySlider from '@/components/PropertySlider'
import ReferenceTagSlider from '@/components/ReferenceTagSlider'
import RightSidebar from '@/components/RightSidebar'
import SubBottomBar from '@/components/SubBottomBar'
import VBackground from '@/components/VBackground'
import VSlides from '@/components/VSlides'
import VirtualTour from '@/components/VirtualTour'
import VTags from '@/components/VTags'
import NotAvailableTag from './components/NotAvailableTag.vue'

// import interactions from '@/mixins/interactions'

import { bus } from './main'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

// No change pls
const BASE_VIEWPORT_WIDTH = 1920
const BASE_VIEWPORT_HEIGHT = 1080
const BASE_FONT_SIZE = 10

export default {
  components: {
    AssetViewer,
    BottomBar,
    HeatMap, 
    HeatMapInfo,
    HeatMapTimeline,
    IssuuModal,
    LivingOptionProperties,
    LivingOptionSubsections,
    LivingOptionTags,
    MasterplanTags,
    PropertyModal,
    PropertySlider,
    ReferenceTagSlider,
    RightSidebar,
    SubBottomBar,
    VBackground,
    VirtualTour,
    VSlides,
    VTags,
    NotAvailableTag
  },

  // mixins: [interactions],

  data () {
    return {
      activeInteractiveSlideId: null,
      appTimeout: null,
      pathway: '',
      routeName: '',
      isAnimating: false,
    }
  },

  computed: {
    ...mapGetters({
      activeSubsection: 'property/activeSubsection',
      language: 'language',
      playbackId: 'interactions/playbackId',
      playbackIndex: 'interactions/playbackIndex',
      sessionPlayback: 'interactions/sessionPlayback',
      showHeat: 'interactions/showHeat',
      showPlayback: 'interactions/showPlayback',
      styles: 'styleTemplate'
    }),

    referenceTags () {
      let tags =  this.$store.getters.activePageTags.filter(tag => tag && tag.type === 'reference')
      tags = _.orderBy(tags, ['pos.x'], ['asc'])

      if (this.language.rtl) {
        tags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = this.tags.length - index
          }
        })
        return tags
      } else {
        tags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = index + 1
          }
        })
        return tags
      }
    },

    sortedAnimationQueue () {
      return this.$store.getters.sortedAnimationQueue
    },

    isAppReady () {
      let isLiveSectionReady = false

      if (
        typeof this.$store.state.navigation[0] !== 'undefined' && 
        typeof this.$store.getters.activeBottomBar !== 'undefined' &&
        typeof this.$store.getters.activeSubBottomBar !== 'undefined' &&
        typeof this.$store.state.property.livingOptions !== 'undefined' && 
        typeof this.$store.getters['property/activeSubsection'] !== 'undefined' &&
        typeof this.$store.getters['property/activeProperties'] !== 'undefined'
      ) {
        isLiveSectionReady = true
      }

      return this.$store.state.config && this.$store.state.pages && this.$store.state.navigation && isLiveSectionReady
    },

    isBottomBarVisible () {
      return this.$store.state.isBottomBarVisible
    },

    styleVars () {
      return {
        '--brand-color': this.styles['right-sidebar']['background-colour'],
        '--font-family': this.styles['general']['font-family'],
      }
    },
  },

  watch: {
    isAppReady () {
      setTimeout(() => {
        bus.$emit('startAnimations')
      }, 100)
    }
  },

  methods: {
    ...mapActions([
      'fetchData',
      'fetchMatterportAvailability',
    ]),

    // We use cookies to set some variables on a per browser level
    // For example - one touchscreen might need to trigger video wall videos
    // This touchscreen will load http://localhost?triggerVideos=true which will set the required variable
    // This is then set as a session cookie so even refreshing to http://localhost/#/ wont lose the settings    
    handleCookies () {
      if (typeof this.$route.query.triggerVideos !== 'undefined') { document.cookie = `triggerVideos=${this.$route.query.triggerVideos}`}
      const triggerVideos = document.cookie.replace(/(?:(?:^|.*;\s*)triggerVideos\s*=\s*([^;]*).*$)|^.*$/, '$1') == 'true' ? true : false
      if (typeof triggerVideos !== 'undefined') { this.$store.state.triggerVideos = triggerVideos }

      if (typeof this.$route.query.hideLiveHere !== 'undefined') { document.cookie = `hideLiveHere=${this.$route.query.hideLiveHere}`}
      const hideLiveHere = document.cookie.replace(/(?:(?:^|.*;\s*)hideLiveHere\s*=\s*([^;]*).*$)|^.*$/, '$1') == 'true' ? true : false
      if (typeof hideLiveHere !== 'undefined') { this.$store.state.hideLiveHere = hideLiveHere }

      if (typeof this.$route.query.deviceName !== 'undefined') { document.cookie = `deviceName=${this.$route.query.deviceName}`}
      const deviceName = document.cookie.replace(/(?:(?:^|.*;\s*)deviceName\s*=\s*([^;]*).*$)|^.*$/, '$1')
      if (typeof deviceName !== 'undefined') { this.$store.state.deviceName = deviceName }

      if (typeof this.$route.query.selector !== 'undefined') { document.cookie = `selector=${this.$route.query.selector}`}
      const selector = document.cookie.replace(/(?:(?:^|.*;\s*)selector\s*=\s*([^;]*).*$)|^.*$/, '$1')
      if (typeof selector !== 'undefined') { this.$store.state.selector = selector }

      if (typeof this.$route.query.videoWallHost !== 'undefined') { document.cookie = `videoWallHost=${this.$route.query.videoWallHost}`}
      const videoWallHost = document.cookie.replace(/(?:(?:^|.*;\s*)videoWallHost\s*=\s*([^;]*).*$)|^.*$/, '$1')
      if (typeof videoWallHost !== 'undefined') { this.$store.state.videoWallHost = videoWallHost }      

      if (typeof this.$route.query.lightswarmHost !== 'undefined') { document.cookie = `lightswarmHost=${this.$route.query.lightswarmHost}`}
      const lightswarmHost = document.cookie.replace(/(?:(?:^|.*;\s*)lightswarmHost\s*=\s*([^;]*).*$)|^.*$/, '$1')
      if (typeof lightswarmHost !== 'undefined') { this.$store.state.lightswarmHost = lightswarmHost }      

      if (typeof this.$route.query.homeUrl !== 'undefined') { document.cookie = `homeUrl=${this.$route.query.homeUrl}`}
      const homeUrl = document.cookie.replace(/(?:(?:^|.*;\s*)homeUrl\s*=\s*([^;]*).*$)|^.*$/, '$1')
      if (typeof homeUrl !== 'undefined') { this.$store.state.homeUrl = homeUrl }            
    },

    resetAppTimeout () {
      if (this.appTimeout) {
        clearInterval(this.appTimeout)
      }

      if (process.env.VUE_APP_ENV === 'production') {
        this.appTimeout = setInterval(() => {
          this.resetInteractionSession()

          if (this.$store.state.homeUrl) {
            window.location.href = this.$store.state.homeUrl
          } else {
            window.location.href = window.location.origin
          }
        }, 1000 * 60 * 5)
      }
    },

    // Super important: The base font size is used to position and size everything using REM at different screen sizes
    setBaseFontSize () {
      const widthScale = window.innerWidth / BASE_VIEWPORT_WIDTH
      const heightScale = window.innerHeight / BASE_VIEWPORT_HEIGHT

      if (widthScale < heightScale) {
        this.$store.state.appScale = widthScale
      } else {
        this.$store.state.appScale = heightScale
      }

      document.documentElement.style.fontSize = `${this.$store.state.appScale * BASE_FONT_SIZE}px`
    },
      
    initSnitch () {
      // Snitch every minute
      setInterval(() => {
        this.$store.dispatch('snitch')
      }, 60 * 1000)
    }
  },

  created () {
    this.fetchData()
    this.fetchMatterportAvailability()
  },

  mounted () {
    // set style template
    const styleTemplateName = process.env.VUE_APP_STYLE_TEMPLATE
    const styleTemplate = require(`../style-templates/${styleTemplateName}.json`)
    this.$store.commit('SET_STYLE_TEMPLATE', styleTemplate)
    
    this.handleCookies()
    this.resetAppTimeout()

    if (this.$store.state.deviceName) {
      this.initSnitch()
    }    

    // Debounce would probably be more performant but 🤷, throttle feels nicer
    window.addEventListener('resize', _.throttle(() => {
      this.setBaseFontSize()
    }, 100))
    this.setBaseFontSize()


    bus.$on('startAnimations', () => {
      if (this.sortedAnimationQueue.length) {
        bus.$emit(`animate${this.sortedAnimationQueue[0]}`)
      }
    })

    bus.$on('finishedAnimation', () => {
      this.$store.commit('advanceAnimationQueue', this.sortedAnimationQueue[0])
      if (this.sortedAnimationQueue.length) {
        bus.$emit(`animate${this.sortedAnimationQueue[0]}`)
      } else {
        bus.$emit('finishedAllAnimations')
      }
    })
  }
}
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/_animations.scss";
@import "@/styles/_type.scss";

html {
  background-color: #333;
  color: #fff;
  font-size: 62.5%;
  font-weight: 400;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

body {
  display: flex;
  height: 100vh;
  margin: 0;
}

#app {
  background-color: #333;
  font-family: $touchscreen-font;
  height: 108rem;
  margin: auto;
  overflow: hidden;
  pointer-events: auto !important;
  position: relative;
  width: 192rem;
}

* {
  box-sizing: border-box;
  outline: none;
  pointer-events: none;
  user-select: none;
  -webkit-font-smoothing: subpixel-antialiased;
}

iframe {
    pointer-events: auto;
    user-select: auto;
}

.touch {
  pointer-events: auto !important;

  .is-animating & {
    pointer-events: none !important;
  }
}

a {
  color: $white;
  text-decoration: none;
}

p {
  margin-bottom: .6em;
  margin-top: 0;
}

button {
  border: none;
  background-color: transparent;
  color: $white;
  font-family: $touchscreen-font;
  letter-spacing: 0.05em;
}

.main {
  height: 98rem;
  left: 0;
  position: absolute;
  top: 0;
  width: 159.5rem;
}

.transition-overlay {
  animation: overlay-reveal 1s forwards 1s;
  background: #333;
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10000;
}

.overlay {
  background: transparent;
  height: 100%;
  pointer-events: none;
  position: relative;
  z-index: 1000;

  &__outer {
    &::before,
    &::after {
      bottom: 0;
      content: '';
      opacity: .5;
      position: absolute;
      top: 0;
      width: $overlay-gradient-width;
    }  

    &::before {
      background: linear-gradient(90deg, 
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.738) 19%,
        rgba(0, 0, 0, 0.541) 34%,
        rgba(0, 0, 0, 0.382) 47%,
        rgba(0, 0, 0, 0.278) 56.5%,
        rgba(0, 0, 0, 0.194) 65%,
        rgba(0, 0, 0, 0.126) 73%,
        rgba(0, 0, 0, 0.075) 80.2%,
        rgba(0, 0, 0, 0.042) 86.1%,
        rgba(0, 0, 0, 0.021) 91%,
        rgba(0, 0, 0, 0.008) 95.2%,
        rgba(0, 0, 0, 0.002) 98.2%,
        rgba(0, 0, 0, 0) 100%
      );
      left: 0;
    }

    &::after {
      background: linear-gradient(270deg, 
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.738) 19%,
        rgba(0, 0, 0, 0.541) 34%,
        rgba(0, 0, 0, 0.382) 47%,
        rgba(0, 0, 0, 0.278) 56.5%,
        rgba(0, 0, 0, 0.194) 65%,
        rgba(0, 0, 0, 0.126) 73%,
        rgba(0, 0, 0, 0.075) 80.2%,
        rgba(0, 0, 0, 0.042) 86.1%,
        rgba(0, 0, 0, 0.021) 91%,
        rgba(0, 0, 0, 0.008) 95.2%,
        rgba(0, 0, 0, 0.002) 98.2%,
        rgba(0, 0, 0, 0) 100%
      );
      right: 0;
    }
  }

  &__inner {
    &::after {
      background: linear-gradient(270deg, 
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.738) 19%,
        rgba(0, 0, 0, 0.541) 34%,
        rgba(0, 0, 0, 0.382) 47%,
        rgba(0, 0, 0, 0.278) 56.5%,
        rgba(0, 0, 0, 0.194) 65%,
        rgba(0, 0, 0, 0.126) 73%,
        rgba(0, 0, 0, 0.075) 80.2%,
        rgba(0, 0, 0, 0.042) 86.1%,
        rgba(0, 0, 0, 0.021) 91%,
        rgba(0, 0, 0, 0.008) 95.2%,
        rgba(0, 0, 0, 0.002) 98.2%,
        rgba(0, 0, 0, 0) 100%
      );
      bottom: 10rem;
      content: '';
      opacity: .5;
      position: absolute;
      right: 32.5rem;
      top: 0;
      width: $overlay-gradient-width;
    }
  }

  &__sidebar {
    &::before {
      background: linear-gradient(90deg, 
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0.738) 19%,
        rgba(0, 0, 0, 0.541) 34%,
        rgba(0, 0, 0, 0.382) 47%,
        rgba(0, 0, 0, 0.278) 56.5%,
        rgba(0, 0, 0, 0.194) 65%,
        rgba(0, 0, 0, 0.126) 73%,
        rgba(0, 0, 0, 0.075) 80.2%,
        rgba(0, 0, 0, 0.042) 86.1%,
        rgba(0, 0, 0, 0.021) 91%,
        rgba(0, 0, 0, 0.008) 95.2%,
        rgba(0, 0, 0, 0.002) 98.2%,
        rgba(0, 0, 0, 0) 100%
      );
      bottom: 10rem;
      content: '';
      opacity: .5;
      position: absolute;
      right: 30rem;
      top: 0;
      width: $overlay-gradient-width;
    }
  }
}

.interaction {
  animation: interaction 1.2s forwards;
  opacity: .8;
  pointer-events: none;
  position: fixed;
  transform-origin: 0% 0%;
  z-index: 20000;

  &::after {
    content: '';
    background: hotpink;
    border-radius: 50%;
    display: block;
    height: 4rem;
    transform: translate(-50%, -50%);
    width: 4rem;
  }
}

@keyframes interaction {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  10% {
    opacity: 1;
    transform: scale(.5);
  }
  45% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.1);
  }
}

.interaction-drag {
  animation: interaction-drag 2s forwards;
  opacity: 1;
  pointer-events: none;
  position: fixed;
  transform-origin: 0% 0%;
  z-index: 20000;

  &::after {
    content: '';
    background: greenyellow;
    border-radius: 50%;
    display: block;
    height: 1rem;
    transform: translate(-50%, -50%);
    width: 1rem;
  }
}

@keyframes interaction-drag {
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0);
  }
}

.na {
  cursor: not-allowed;
  // pointer-events: none;
}
</style>
